import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '../useFetch'
import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import { BlockConflict, BlockModel } from '../../types/court/court'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'
import { formatDateTimesInText } from '../../utils/dateUtils'

export function useBlockConflicts() {
  const { t, i18n } = useTranslation('exceptions')
  const { authInfo } = useAuth()
  const { poster } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetState = () => {
    setLoading(false)
    setError(null)
  }

  const getBlockConflicts = async (request: BlockModel) => {
    setLoading(true)
    setError(null)

    try {
      // A new block may cover several courts; an edited one keeps its single court.
      const conflicts = request.courtIds?.length
        ? await poster<BlockConflict[]>(
            `${CONFIG.BOUNCE_API}/api/v1/blocks/conflicts`,
            {
              courtIds: request.courtIds,
              startTime: request.startTime,
              endTime: request.endTime,
              reason: request.reason,
              notes: request.notes,
            },
            'application/json'
          )
        : await poster<BlockConflict[]>(
            `${CONFIG.BOUNCE_API}/api/v1/courts/${request.courtId}/blocks/conflicts`,
            request,
            'application/json'
          )

      return conflicts ?? []
    } catch (caughtError) {
      console.error(caughtError)

      setError(
        caughtError instanceof ApiError
          ? formatDateTimesInText(
              getErrorMessage(caughtError.data),
              i18n.language
            )
          : t('unknown')
      )

      return undefined
    } finally {
      setLoading(false)
    }
  }

  return { getBlockConflicts, loading, error, resetState }
}
